import { supabase } from '../lib/supabase';

export interface ResultadoEliminacion {
  exito: boolean;
  mensaje: string;
  registros_eliminados: number;
}

export interface ImpactoEliminacion {
  tipo_entidad: 'provincia' | 'diocesis' | 'lumisial';
  entidad_id: string;
  entidad_nombre: string;
  impacto: {
    lumisiales?: number;
    miembros?: number;
    movimientos?: number;
    actividades?: number;
    diocesis?: number;
  };
}

export class AdminGlobalService {
  private static cacheAdminGlobal: { resultado: boolean; timestamp: number } | null = null;
  private static readonly CACHE_DURATION = 30000; // 30 segundos de cache

  /**
   * Verifica si el usuario actual es admin global
   */
  static async verificarAdminGlobal(): Promise<boolean> {
    try {
      // Verificar cache primero
      const timestampActual = Date.now();
      if (this.cacheAdminGlobal && (timestampActual - this.cacheAdminGlobal.timestamp) < this.CACHE_DURATION) {
        return this.cacheAdminGlobal.resultado;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        this.cacheAdminGlobal = { resultado: false, timestamp: timestampActual };
        return false;
      }

      const { data, error } = await supabase
        .from('miembros')
        .select(`
          id,
          nombres,
          apellidos,
          estado_miembro,
          roles!inner(
            nombre,
            nivel
          )
        `)
        .eq('user_id', user.id)
        .eq('roles.nombre', 'admin_global')
        .eq('estado_miembro', 'activo')
        .single();

      const resultado = !error && !!data;
      
      // Guardar en cache
      this.cacheAdminGlobal = { resultado, timestamp: timestampActual };

      return resultado;
    } catch (error) {
      console.error('Error verificando admin global:', error);
      const timestampActual = Date.now();
      this.cacheAdminGlobal = { resultado: false, timestamp: timestampActual };
      return false;
    }
  }

  /**
   * Obtiene información detallada del impacto de eliminación
   */
  static async obtenerImpactoEliminacion(
    tipoEntidad: 'provincia' | 'diocesis' | 'lumisial',
    entidadId: string
  ): Promise<ImpactoEliminacion | null> {
    try {
      let impacto: ImpactoEliminacion | null = null;

      if (tipoEntidad === 'lumisial') {
        // Obtener información del lumisial y sus dependencias
        const { data: lumisial, error: errorLumisial } = await supabase
          .from('lumisiales')
          .select(`
            id,
            nombre,
            diocesis_id,
            provincia_eclesiastica_id
          `)
          .eq('id', entidadId)
          .eq('activo', true)
          .single();

        if (errorLumisial || !lumisial) return null;

        // Contar miembros
        const { count: miembros } = await supabase
          .from('miembros')
          .select('*', { count: 'exact', head: true })
          .eq('lumisial_actual_id', entidadId)
          .eq('activo', true);

        // Contar movimientos
        const { count: movimientos } = await supabase
          .from('movimientos')
          .select('*', { count: 'exact', head: true })
          .eq('lumisial_id', entidadId);

        // Contar actividades económicas
        const { count: actividades } = await supabase
          .from('actividades_economicas')
          .select('*', { count: 'exact', head: true })
          .eq('lumisial_id', entidadId);

        impacto = {
          tipo_entidad: 'lumisial',
          entidad_id: lumisial.id,
          entidad_nombre: lumisial.nombre,
          impacto: {
            miembros: miembros || 0,
            movimientos: movimientos || 0,
            actividades: actividades || 0
          }
        };

      } else if (tipoEntidad === 'diocesis') {
        // Obtener información de la diócesis
        const { data: diocesis, error: errorDiocesis } = await supabase
          .from('diocesis')
          .select(`
            id,
            nombre,
            provincia_eclesiastica_id
          `)
          .eq('id', entidadId)
          .eq('activo', true)
          .single();

        if (errorDiocesis || !diocesis) return null;

        // Contar lumisiales activos
        const { count: lumisiales } = await supabase
          .from('lumisiales')
          .select('*', { count: 'exact', head: true })
          .eq('diocesis_id', entidadId)
          .eq('activo', true);

        impacto = {
          tipo_entidad: 'diocesis',
          entidad_id: diocesis.id,
          entidad_nombre: diocesis.nombre,
          impacto: {
            lumisiales: lumisiales || 0
          }
        };

      } else if (tipoEntidad === 'provincia') {
        // Obtener información de la provincia
        const { data: provincia, error: errorProvincia } = await supabase
          .from('provincias_eclesiasticas')
          .select(`
            id,
            nombre,
            continente
          `)
          .eq('id', entidadId)
          .eq('activo', true)
          .single();

        if (errorProvincia || !provincia) return null;

        // Contar diócesis activas
        const { count: diocesis } = await supabase
          .from('diocesis')
          .select('*', { count: 'exact', head: true })
          .eq('provincia_eclesiastica_id', entidadId)
          .eq('activo', true);

        impacto = {
          tipo_entidad: 'provincia',
          entidad_id: provincia.id,
          entidad_nombre: provincia.nombre,
          impacto: {
            diocesis: diocesis || 0
          }
        };
      }

      return impacto;
    } catch (error) {
      // No tragar: `null` ya significa "entidad no encontrada" en las ramas de arriba
      // (`if (!diocesis) return null`). Si un fallo de sistema devolviera lo mismo, el
      // catch de EliminacionDefinitivaModal —que sí reporta a Sentry— nunca se ejecutaría,
      // porque acá no se relanza nada que llegue a propagarse. Propagar para que el
      // llamador distinga "no existe" de "no se pudo consultar".
      console.error('Error obteniendo impacto de eliminación:', error);
      throw error;
    }
  }

  /**
   * Elimina definitivamente una entidad (provincia, diócesis o lumisial)
   */
  static async eliminarEntidadDefinitivamente(
    tipoEntidad: 'provincia' | 'diocesis' | 'lumisial',
    entidadId: string,
    confirmacion: boolean
  ): Promise<ResultadoEliminacion> {
    try {
      // Verificar que sea admin global
      const esAdminGlobal = await this.verificarAdminGlobal();
      if (!esAdminGlobal) {
        return {
          exito: false,
          mensaje: 'ACCESO_DENEGADO: Solo el administrador global puede ejecutar esta función',
          registros_eliminados: 0
        };
      }

      // Ejecutar la función de eliminación
      const { data, error } = await supabase.rpc('eliminar_entidad_definitivamente', {
        p_tipo_entidad: tipoEntidad,
        p_entidad_id: entidadId,
        p_confirmacion_admin: confirmacion
      });

      if (error) {
        console.error('Error en eliminación definitiva:', error);
        return {
          exito: false,
          mensaje: `ERROR: ${error.message}`,
          registros_eliminados: 0
        };
      }

      // La función retorna una tabla, necesitamos extraer el primer resultado
      const resultado = data?.[0];
      if (!resultado) {
        return {
          exito: false,
          mensaje: 'ERROR: No se recibió respuesta del servidor',
          registros_eliminados: 0
        };
      }

      return {
        exito: resultado.exito,
        mensaje: resultado.mensaje,
        registros_eliminados: resultado.registros_eliminados
      };

    } catch (error) {
      console.error('Error en eliminación definitiva:', error);
      return {
        exito: false,
        mensaje: `ERROR_INESPERADO: ${error instanceof Error ? error.message : 'Error desconocido'}`,
        registros_eliminados: 0
      };
    }
  }

  /**
   * Obtiene el historial de eliminaciones realizadas
   */
  static async obtenerHistorialEliminaciones(
    limite: number = 50,
    offset: number = 0
  ): Promise<any[]> {
    try {
      const esAdminGlobal = await this.verificarAdminGlobal();
      if (!esAdminGlobal) return [];

      const { data, error } = await supabase
        .from('log_eliminaciones_definitivas')
        .select(`
          id,
          tipo_entidad,
          entidad_nombre,
          fecha_eliminacion,
          registros_eliminados,
          resumen_eliminacion,
          miembros!inner(
            nombre_completo,
            email
          )
        `)
        .order('fecha_eliminacion', { ascending: false })
        .range(offset, offset + limite - 1);

      if (error) {
        console.error('Error obteniendo historial:', error);
        return [];
      }

      return data || [];
    } catch (error) {
      // deuda: un historial que falla se ve igual que un historial vacío.
      // techo: aguanta mientras sea consulta informativa, no base de una decisión.
      // disparador: si el historial pasa a sustentar una acción (auditoría, reversa), lanzar.
      console.error('Error obteniendo historial de eliminaciones:', error);
      return [];
    }
  }

  /**
   * Obtiene estadísticas de eliminaciones
   */
  static async obtenerEstadisticasEliminaciones(): Promise<{
    total_eliminaciones: number;
    por_tipo: Record<string, number>;
    ultimo_mes: number;
  }> {
    try {
      const esAdminGlobal = await this.verificarAdminGlobal();
      if (!esAdminGlobal) {
        return {
          total_eliminaciones: 0,
          por_tipo: {},
          ultimo_mes: 0
        };
      }

      // Total de eliminaciones
      const { count: total } = await supabase
        .from('log_eliminaciones_definitivas')
        .select('*', { count: 'exact', head: true });

      // Eliminaciones por tipo
      const { data: porTipo } = await supabase
        .from('log_eliminaciones_definitivas')
        .select('tipo_entidad')
        .order('tipo_entidad');

      // Eliminaciones del último mes
      const fechaHaceUnMes = new Date();
      fechaHaceUnMes.setMonth(fechaHaceUnMes.getMonth() - 1);

      const { count: ultimoMes } = await supabase
        .from('log_eliminaciones_definitivas')
        .select('*', { count: 'exact', head: true })
        .gte('fecha_eliminacion', fechaHaceUnMes.toISOString());

      // Agrupar por tipo
      const agrupadoPorTipo: Record<string, number> = {};
      porTipo?.forEach(item => {
        agrupadoPorTipo[item.tipo_entidad] = (agrupadoPorTipo[item.tipo_entidad] || 0) + 1;
      });

      return {
        total_eliminaciones: total || 0,
        por_tipo: agrupadoPorTipo,
        ultimo_mes: ultimoMes || 0
      };

    } catch (error) {
      console.error('Error obteniendo estadísticas:', error);
      return {
        total_eliminaciones: 0,
        por_tipo: {},
        ultimo_mes: 0
      };
    }
  }

  /**
   * Valida el texto de confirmación de seguridad
   */
  static validarTextoConfirmacion(texto: string): boolean {
    const textoRequerido = 'ELIMINAR_DEFINITIVAMENTE';
    return texto.trim().toUpperCase() === textoRequerido;
  }

  /**
   * Genera un resumen legible del impacto
   */
  static generarResumenImpacto(impacto: ImpactoEliminacion): string {
    const partes: string[] = [];
    
    if (impacto.impacto.lumisiales && impacto.impacto.lumisiales > 0) {
      partes.push(`${impacto.impacto.lumisiales} lumisial(es)`);
    }
    
    if (impacto.impacto.miembros && impacto.impacto.miembros > 0) {
      partes.push(`${impacto.impacto.miembros} miembro(s)`);
    }
    
    if (impacto.impacto.movimientos && impacto.impacto.movimientos > 0) {
      partes.push(`${impacto.impacto.movimientos} movimiento(s) económico(s)`);
    }
    
    if (impacto.impacto.actividades && impacto.impacto.actividades > 0) {
      partes.push(`${impacto.impacto.actividades} actividad(es) económica(s)`);
    }
    
    if (impacto.impacto.diocesis && impacto.impacto.diocesis > 0) {
      partes.push(`${impacto.impacto.diocesis} diócesis`);
    }
    
    if (partes.length === 0) {
      return 'No hay datos dependientes';
    }
    
    return `Se eliminarán: ${partes.join(', ')}`;
  }
}

export default AdminGlobalService;
